import { useContext } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAxiosClient } from "../API/index";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../General/AppPreferencesProvider";
import {
  RUN_FIELD,
  serializeRequestParams,
  analystRunsQuery,
  type AnalystRunsState,
} from "./analystRunsQueryParams";
import type {
  AnalystRunFields,
  AnalystRunsResponse,
  AnalystInstancesResponse,
  AnalystInstancesDetailsResponse,
  AgentDefinitionsResponse,
  AnalystInstanceMetadata,
  AnalystAutomationsResponse,
  CreateAnalystAutomationRequest,
  CreateAnalystAutomationResponse,
  UpdateAnalystAutomationRequest,
  AnalystCreditBatch,
  AvailableIntegrationsResponse,
  AddAnalystInstanceRequest,
  AddAnalystInstanceResponse,
  UpdateAnalystInstanceRequest,
  CreditPack,
  CreditPacksResponse,
  CreateCreditPackRequest,
} from "./types";

const CREDITS_POLL_INTERVAL_MS = 20000;

const AI_ANALYST_AUTOMATIONS_PATH = "/automations/ai-analysts";

export const analystRunsKeys = {
  runs: ["agents", "analyst-runs"] as const,
  runsExist: ["agents", "analyst-runs", "exist"] as const,
  instances: ["agents", "analyst-instances"] as const,
  instancesDetails: ["agents", "analyst-instances", "details"] as const,
  definitions: ["agents", "definitions"] as const,
  credits: ["agents", "analyst-credits"] as const,
  creditBatches: ["agents", "analyst-credits", "batches"] as const,
  configs: ["internal", "analyst-instances"] as const,
  availableIntegrations: [
    "internal",
    "analyst-instances",
    "available-integrations",
  ] as const,
  creditPacks: ["internal", "analyst-credits"] as const,
  allAutomations: ["automations", "ai-analysts", "all"] as const,
  automations: (agentInstanceId?: string) =>
    ["automations", "ai-analysts", agentInstanceId] as const,
  automationsRoot: ["automations", "ai-analysts"] as const,
  instanceInfo: (agentInstanceId?: string) =>
    ["agents", "analyst-instances", agentInstanceId, "info"] as const,
};

export type WarningState = "OK" | "WARNING_80" | "WARNING_90" | "EXHAUSTED";

export interface AnalystCreditsResponse {
  credits_purchased: number;
  credits_used: number;
  expiry_date: string;
  warning_state: WarningState;
}

export const useAnalystApiEnabled = (): boolean => {
  const {
    appPreferences: { useAI },
    isAllowed,
  } = useContext(AppPreferencesContext);
  return !!useAI && isAllowed(userActionsEnum.RUN_AGENT_ANALYSIS);
};

const ANALYST_RUNS_PATH_URL = "/agents/analyst-runs";

export const useAnalystRuns = (urlState: AnalystRunsState, enabled = true) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();
  const params = analystRunsQuery.toPaginatedRequestParams(urlState);

  return useQuery<AnalystRunsResponse>({
    queryKey: [...analystRunsKeys.runs, params],
    queryFn: async () => {
      const response = await client.get<AnalystRunsResponse>(
        ANALYST_RUNS_PATH_URL,
        { params, paramsSerializer: serializeRequestParams }
      );
      return response.data;
    },
    enabled: isAnalystEnabled && enabled,
    placeholderData: keepPreviousData,
  });
};

type AnalystRunsExistResponse = AnalystRunsResponse<
  Pick<AnalystRunFields, "run_id">
>;

export const useAnalystRunsExist = (enabled = true) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();

  return useQuery<AnalystRunsExistResponse>({
    queryKey: analystRunsKeys.runsExist,
    queryFn: async () => {
      const response = await client.get<AnalystRunsExistResponse>(
        ANALYST_RUNS_PATH_URL,
        { params: { limit: 1, select: RUN_FIELD.RUN_ID } }
      );
      return response.data;
    },
    enabled: isAnalystEnabled && enabled,
    staleTime: Infinity,
  });
};

export const useAnalystInstances = (enabled = true) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();

  return useQuery<AnalystInstancesResponse>({
    queryKey: analystRunsKeys.instances,
    queryFn: async () => {
      const response = await client.get<AnalystInstancesResponse>(
        "/agents/analyst-instances"
      );
      return response.data;
    },
    enabled: isAnalystEnabled && enabled,
  });
};

export const useAnalystInstancesDetails = (enabled = true) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();

  return useQuery<AnalystInstancesDetailsResponse>({
    queryKey: analystRunsKeys.instancesDetails,
    queryFn: async () => {
      const response = await client.get<AnalystInstancesDetailsResponse>(
        "/agents/analyst-instances/details"
      );
      return response.data;
    },
    enabled: isAnalystEnabled && enabled,
  });
};

const useAgentDefinitionsQuery = (enabled: boolean) => {
  const client = useAxiosClient({ domain: "ai" });

  return useQuery<AgentDefinitionsResponse>({
    queryKey: analystRunsKeys.definitions,
    queryFn: async () => {
      const response = await client.get<AgentDefinitionsResponse>(
        "/agents/definitions"
      );
      return response.data;
    },
    enabled,
  });
};

export const useAgentDefinitions = () =>
  useAgentDefinitionsQuery(useAnalystApiEnabled());

export const useCncAgentDefinitions = () => useAgentDefinitionsQuery(true);

export const useAllAnalystAutomations = (enabled = true) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();

  return useQuery<AnalystAutomationsResponse>({
    queryKey: analystRunsKeys.allAutomations,
    queryFn: async () => {
      const response = await client.get<AnalystAutomationsResponse>(
        AI_ANALYST_AUTOMATIONS_PATH
      );
      return response.data;
    },
    enabled: isAnalystEnabled && enabled,
  });
};

export const useAnalystInstanceMetadata = (agentInstanceId?: string) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();

  return useQuery<AnalystInstanceMetadata>({
    queryKey: analystRunsKeys.instanceInfo(agentInstanceId),
    queryFn: async () => {
      const response = await client.get<AnalystInstanceMetadata>(
        `/agents/analyst-instances/${agentInstanceId}`
      );
      return response.data;
    },
    enabled: isAnalystEnabled && !!agentInstanceId,
    retry: false,
  });
};

export const useAnalystAutomations = (agentInstanceId?: string) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();

  return useQuery<AnalystAutomationsResponse>({
    queryKey: analystRunsKeys.automations(agentInstanceId),
    queryFn: async () => {
      const response = await client.get<AnalystAutomationsResponse>(
        AI_ANALYST_AUTOMATIONS_PATH,
        { params: { entity_id: agentInstanceId } }
      );
      return response.data;
    },
    enabled: isAnalystEnabled && !!agentInstanceId,
  });
};

interface DeleteAnalystAutomationData {
  automationId: string;
}

export const useDeleteAnalystAutomation = () => {
  const client = useAxiosClient({ domain: "ai" });
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteAnalystAutomationData>({
    mutationFn: async ({ automationId }) => {
      await client.delete(`${AI_ANALYST_AUTOMATIONS_PATH}/${automationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: analystRunsKeys.automationsRoot,
      });
    },
  });
};

interface CreateAnalystAutomationData {
  agentInstanceId: string;
  body: CreateAnalystAutomationRequest;
}

export const useCreateAnalystAutomation = () => {
  const client = useAxiosClient({ domain: "ai" });
  const queryClient = useQueryClient();

  return useMutation<
    CreateAnalystAutomationResponse,
    Error,
    CreateAnalystAutomationData
  >({
    mutationFn: async ({ agentInstanceId, body }) => {
      const response = await client.post<CreateAnalystAutomationResponse>(
        AI_ANALYST_AUTOMATIONS_PATH,
        { entity_id: agentInstanceId, ...body }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: analystRunsKeys.automationsRoot,
      });
    },
  });
};

interface UpdateAnalystAutomationData {
  automationId: string;
  body: UpdateAnalystAutomationRequest;
}

export const useUpdateAnalystAutomation = () => {
  const client = useAxiosClient({ domain: "ai" });
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateAnalystAutomationData>({
    mutationFn: async ({ automationId, body }) => {
      await client.patch<void>(
        `${AI_ANALYST_AUTOMATIONS_PATH}/${automationId}`,
        body
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: analystRunsKeys.automationsRoot,
      });
    },
  });
};

export const useAnalystCredits = (enabled = true) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();

  return useQuery<AnalystCreditsResponse>({
    queryKey: analystRunsKeys.credits,
    queryFn: async () => {
      const response = await client.get<AnalystCreditsResponse>(
        "/agents/analyst-credits"
      );
      return response.data;
    },
    enabled: isAnalystEnabled && enabled,
    refetchInterval: CREDITS_POLL_INTERVAL_MS,
  });
};

export const useAnalystCreditBatches = (enabled = true) => {
  const client = useAxiosClient({ domain: "ai" });
  const isAnalystEnabled = useAnalystApiEnabled();

  return useQuery<AnalystCreditBatch[]>({
    queryKey: analystRunsKeys.creditBatches,
    queryFn: async () => {
      const response = await client.get<AnalystCreditBatch[]>(
        "/agents/analyst-credits/batches"
      );
      return response.data;
    },
    enabled: isAnalystEnabled && enabled,
    refetchInterval: CREDITS_POLL_INTERVAL_MS,
  });
};

export const useAnalystInstancesConfigs = (targetAccountUuid?: string) => {
  const client = useAxiosClient({ targetAccountUuid });

  return useQuery<AnalystInstancesResponse>({
    queryKey: [...analystRunsKeys.configs, targetAccountUuid],
    queryFn: async () => {
      const response = await client.get<AnalystInstancesResponse>(
        "/internal/analyst-instances"
      );
      return response.data;
    },
    select: (data) => ({
      ...data,
      agent_instances: data.agent_instances.map((instance) => ({
        ...instance,
        agent_type: instance.agent_type.toLowerCase(),
      })),
    }),
    enabled: !!targetAccountUuid,
  });
};

export const useAvailableIntegrations = (targetAccountUuid?: string) => {
  const client = useAxiosClient({ targetAccountUuid });

  return useQuery<AvailableIntegrationsResponse>({
    queryKey: [...analystRunsKeys.availableIntegrations, targetAccountUuid],
    queryFn: async () => {
      const response = await client.get<AvailableIntegrationsResponse>(
        "/internal/analyst-instances/available-integrations"
      );
      return response.data;
    },
    enabled: !!targetAccountUuid,
  });
};

export const useCreateAnalystInstance = (targetAccountUuid?: string) => {
  const client = useAxiosClient({ targetAccountUuid });
  const queryClient = useQueryClient();

  return useMutation<
    AddAnalystInstanceResponse,
    Error,
    AddAnalystInstanceRequest
  >({
    mutationFn: async (body) => {
      const response = await client.post<AddAnalystInstanceResponse>(
        "/internal/analyst-instances",
        body
      );
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [...analystRunsKeys.configs, targetAccountUuid],
      }),
  });
};

interface UpdateAnalystInstanceVars {
  agentInstanceId: string;
  body: UpdateAnalystInstanceRequest;
}

export const useUpdateAnalystInstance = (targetAccountUuid?: string) => {
  const client = useAxiosClient({ targetAccountUuid });
  const queryClient = useQueryClient();

  return useMutation<
    AddAnalystInstanceResponse,
    Error,
    UpdateAnalystInstanceVars
  >({
    mutationFn: async ({ agentInstanceId, body }) => {
      const response = await client.patch<AddAnalystInstanceResponse>(
        `/internal/analyst-instances/${agentInstanceId}`,
        body
      );
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [...analystRunsKeys.configs, targetAccountUuid],
      }),
  });
};

export const useAccountCreditPacks = (targetAccountUuid?: string) => {
  const client = useAxiosClient({ targetAccountUuid });

  return useQuery<CreditPacksResponse>({
    queryKey: [...analystRunsKeys.creditPacks, targetAccountUuid],
    queryFn: async () => {
      const response = await client.get<CreditPacksResponse>(
        "/internal/analyst-credits/batches"
      );
      return response.data;
    },
    enabled: !!targetAccountUuid,
    gcTime: 0,
  });
};

export const useCreateCreditPack = (targetAccountUuid?: string) => {
  const client = useAxiosClient({ targetAccountUuid });
  const queryClient = useQueryClient();

  return useMutation<CreditPack, Error, CreateCreditPackRequest>({
    mutationFn: async (body) => {
      const response = await client.post<CreditPack>(
        "/internal/analyst-credits",
        body
      );
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [...analystRunsKeys.creditPacks, targetAccountUuid],
      }),
  });
};
